import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  TrendingUp, 
  Eye, 
  Target, 
  Percent, 
  DollarSign,
  Filter
} from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { 
  SSPDashboardData, 
  SSPFilters, 
  SSPFilterOptions 
} from '../../../types/ssp';
import { getSSPDashboardData, getSSPFilterOptions } from '../../../api/sspApi';
import dayjs from 'dayjs';

// UI Components
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Alert, AlertDescription, AlertTitle } from '../../ui/alert';

// SSP Components
import { SSPFiltersComponent } from './SSPFilters';
import { SSPTable } from './SSPTable';
import { SspNoService } from './SspNoService';

// Chart components (réutiliser ceux d'adblock)
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export const SSPDashboard: React.FC = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  
  // États
  const [dashboardData, setDashboardData] = useState<SSPDashboardData | null>(null);
  const [filterOptions, setFilterOptions] = useState<SSPFilterOptions | null>(null);
  const [filters, setFilters] = useState<SSPFilters>({
    startDate: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD')
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasNoService, setHasNoService] = useState(false);

  // Récupérer les données du dashboard
  const fetchDashboardData = async (currentFilters?: SSPFilters) => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getSSPDashboardData(currentFilters || filters);
      setDashboardData(data.data);
      
      // Détecter si l'utilisateur CLIENT n'a aucun service configuré
      const isEmpty = !data.data.summary.estimatedRevenue && 
                     !data.data.summary.adslotImpressions && 
                     !data.data.summary.adImpressions &&
                     data.data.charts.estimatedRevenue.length === 0;
      setHasNoService(isEmpty && currentUser?.role === 'CLIENT');
    } catch (error: any) {
      console.error('Error fetching SSP dashboard data:', error);
      setError(t('ssp.errorFetchingData'));
    } finally {
      setIsLoading(false);
    }
  };

  // Récupérer les options de filtres
  const fetchFilterOptions = async () => {
    try {
      const options = await getSSPFilterOptions();
      setFilterOptions(options);
    } catch (error: any) {
      console.error('Error fetching SSP filter options:', error);
    }
  };

  // Charger les données initiales
  useEffect(() => {
    fetchFilterOptions();
    fetchDashboardData();
  }, []);

  // Gestionnaire de changement de filtre
  const handleFiltersChange = (newFilters: SSPFilters) => {
    setFilters(newFilters);
    fetchDashboardData(newFilters);
  };

  // Formater les valeurs
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  // Fonction pour créer les cartes de graphiques avec métriques
  const renderChart = (title: string, children: React.ReactNode, metric?: { value: number; type: 'sum' | 'average' | 'currency' | 'percentage' }) => (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-bold flex justify-between items-center">
          <span>{title}</span>
          {metric && (
            <span className="text-sm font-bold text-foreground">
              {metric.type === 'currency' ? formatCurrency(metric.value) : 
               metric.type === 'percentage' ? formatPercentage(metric.value) :
               formatNumber(metric.value)}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-80">
          {children}
        </div>
      </CardContent>
    </Card>
  );

  // Configuration des graphiques
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      }
    },
    scales: {
      x: {
        ticks: {
          maxTicksLimit: 3 // Maximum 3 dates sur l'axe X
        }
      },
      y: {
        beginAtZero: true
      }
    }
  };  // Créer les données de graphique
  const createChartData = (data: any[], label: string, color: string) => {
    console.log('🔍 createChartData - Raw data:', data);
    console.log('🔍 createChartData - First item:', data[0]);
    
    const labels = data.map((item, index) => {
      console.log(`🔍 Processing date ${index}:`, item.date, typeof item.date);
      try {
        const dateObj = new Date(item.date);
        console.log(`🔍 Date object:`, dateObj, dateObj.toString());
        const formatted = dateObj.toLocaleDateString('fr-FR', { month: 'short', day: 'numeric' });
        console.log(`🔍 Formatted date:`, formatted);
        return formatted;
      } catch (error) {
        console.error(`🔥 Error formatting date ${item.date}:`, error);
        return item.date; // Fallback à la date originale
      }
    });
    
    console.log('🔍 Final labels:', labels);
    
    return {
      labels,
      datasets: [
        {
          label,
          data: data.map(item => item.value),
          borderColor: color,
          backgroundColor: color + '20',
          fill: true,
          tension: 0.4,
        },
      ],
    };
  };

  if (!currentUser) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertTitle>{t('common.error')}</AlertTitle>
          <AlertDescription>{t('auth.notAuthenticated')}</AlertDescription>
        </Alert>
      </div>
    );
  }

  // Afficher le composant "aucun service" pour les utilisateurs CLIENT sans configuration
  if (hasNoService && currentUser.role === 'CLIENT' && !isLoading) {
    return <SspNoService />;
  }

  return (
    <div className="min-h-[calc(100vh-7rem)] overflow-auto">
      <div className="p-6 space-y-6">
        {/* En-tête */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold">{t('ssp.title')}</h1>
            <p className="text-muted-foreground mt-1">
              {t('ssp.subtitle')}
            </p>
          </div>
        </div>

        {/* Filtres */}
        <div className="animate-in fade-in duration-500 delay-300">
          {isLoading ? (
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2">
                  <Filter className="h-5 w-5" />
                  {t('common.filters')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="h-10 bg-muted rounded animate-pulse"></div>
                  <div className="h-10 bg-muted rounded animate-pulse"></div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2">
                  <Filter className="h-5 w-5" />
                  {t('common.filters')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <SSPFiltersComponent
                  filters={filters}
                  filterOptions={filterOptions}
                  onChange={handleFiltersChange}
                  isLoading={isLoading}
                />
              </CardContent>
            </Card>
          )}
        </div>

        {/* Messages d'erreur */}
        {error && (
          <Alert variant="destructive">
            <AlertTitle>{t('common.error')}</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Métriques de résumé */}
        {dashboardData && (
          <Card className="animate-in fade-in duration-500 delay-500">
            <CardHeader>
              <CardTitle>{t('ssp.summary.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  <Card className="border-none shadow-sm bg-gradient-to-r from-blue-500 to-blue-600">
                    <CardContent className="p-6 text-white">
                      <div className="flex items-center justify-between space-y-0 pb-2">
                        <h3 className="tracking-tight text-sm font-medium opacity-90">
                          {t('ssp.estimatedRevenue')}
                        </h3>
                        <DollarSign className="h-6 w-6 opacity-90" />
                      </div>
                      <div className="text-2xl font-bold">
                        {formatCurrency(dashboardData.summary.estimatedRevenue)}
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-none shadow-sm bg-gradient-to-r from-green-500 to-green-600">
                    <CardContent className="p-6 text-white">
                      <div className="flex items-center justify-between space-y-0 pb-2">
                        <h3 className="tracking-tight text-sm font-medium opacity-90">
                          {t('ssp.adslotImpressions')}
                        </h3>
                        <Target className="h-6 w-6 opacity-90" />
                      </div>
                      <div className="text-2xl font-bold">
                        {formatNumber(dashboardData.summary.adslotImpressions)}
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-none shadow-sm bg-gradient-to-r from-orange-500 to-orange-600">
                    <CardContent className="p-6 text-white">
                      <div className="flex items-center justify-between space-y-0 pb-2">
                        <h3 className="tracking-tight text-sm font-medium opacity-90">
                          {t('ssp.adImpressions')}
                        </h3>
                        <Eye className="h-6 w-6 opacity-90" />
                      </div>
                      <div className="text-2xl font-bold">
                        {formatNumber(dashboardData.summary.adImpressions)}
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-none shadow-sm bg-gradient-to-r from-red-500 to-red-600">
                    <CardContent className="p-6 text-white">
                      <div className="flex items-center justify-between space-y-0 pb-2">
                        <h3 className="tracking-tight text-sm font-medium opacity-90">
                          {t('ssp.winRate')}
                        </h3>
                        <Percent className="h-6 w-6 opacity-90" />
                      </div>
                      <div className="text-2xl font-bold">
                        {formatPercentage(dashboardData.summary.winRate)}
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-none shadow-sm bg-gradient-to-r from-purple-500 to-purple-600">
                    <CardContent className="p-6 text-white">
                      <div className="flex items-center justify-between space-y-0 pb-2">
                        <h3 className="tracking-tight text-sm font-medium opacity-90">
                          {t('ssp.estimatedRPM')}
                        </h3>
                        <TrendingUp className="h-6 w-6 opacity-90" />
                      </div>
                      <div className="text-2xl font-bold">
                        {formatCurrency(dashboardData.summary.estimatedRPM)}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        {/* Graphiques */}
        {dashboardData && (
          <div className="animate-in fade-in duration-500 delay-700">
            {/* Première ligne : Revenue et AdSlot Impressions */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Revenus estimés */}
              {renderChart(
                t('ssp.estimatedRevenue'),
                <Line 
                  data={createChartData(dashboardData.charts.estimatedRevenue, t('ssp.estimatedRevenue'), '#3b82f6')}
                  options={chartOptions}
                />,
                { 
                  value: dashboardData.charts.estimatedRevenue.reduce((sum, item) => sum + item.value, 0), 
                  type: 'currency' 
                }
              )}

              {/* Impressions des AdSlots */}
              {renderChart(
                t('ssp.adslotImpressions'),
                <Line 
                  data={createChartData(dashboardData.charts.adslotImpressions, t('ssp.adslotImpressions'), '#10b981')}
                  options={chartOptions}
                />,
                { 
                  value: dashboardData.charts.adslotImpressions.reduce((sum, item) => sum + item.value, 0), 
                  type: 'sum' 
                }
              )}
            </div>

            {/* Deuxième ligne : Ad Impressions, Win Rate, Estimated RPM */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Impressions des annonces */}
              {renderChart(
                t('ssp.adImpressions'),
                <Line 
                  data={createChartData(dashboardData.charts.adImpressions, t('ssp.adImpressions'), '#f59e0b')}
                  options={chartOptions}
                />,
                { 
                  value: dashboardData.charts.adImpressions.reduce((sum, item) => sum + item.value, 0), 
                  type: 'sum' 
                }
              )}

              {/* Win Rate */}
              {renderChart(
                t('ssp.winRate'),
                <Line 
                  data={createChartData(dashboardData.charts.winRate, t('ssp.winRate'), '#ef4444')}
                  options={chartOptions}
                />,
                { 
                  value: dashboardData.charts.winRate.length > 0 ? 
                    dashboardData.charts.winRate.reduce((sum, item) => sum + item.value, 0) / dashboardData.charts.winRate.length : 0, 
                  type: 'percentage' 
                }
              )}

              {/* RPM estimé */}
              {renderChart(
                t('ssp.estimatedRPM'),
                <Line 
                  data={createChartData(dashboardData.charts.estimatedRPM, t('ssp.estimatedRPM'), '#8b5cf6')}
                  options={chartOptions}
                />,
                { 
                  value: dashboardData.charts.estimatedRPM.length > 0 ? 
                    dashboardData.charts.estimatedRPM.reduce((sum, item) => sum + item.value, 0) / dashboardData.charts.estimatedRPM.length : 0, 
                  type: 'currency' 
                }
              )}
            </div>
          </div>
        )}

        {/* Tableau de données quotidiennes */}
        {dashboardData && (
          <div className="animate-in fade-in duration-500 delay-900">
            <SSPTable
              dailyData={dashboardData.charts}
              isLoading={isLoading}
            />
          </div>
        )}

        {/* État de chargement */}
        {isLoading && !dashboardData && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {Array(5).fill(0).map((_, i) => (
              <Card key={i}>
                <CardHeader className="space-y-0 pb-2">
                  <div className="h-4 bg-muted rounded animate-pulse"></div>
                </CardHeader>
                <CardContent>
                  <div className="h-8 bg-muted rounded animate-pulse"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};